/**
 * The screen's theme for a signed-in session (`lib/theme.ts`): one place holds the choice, keeps
 * `<html data-theme>` in step with it and, while it is Match device, with the device's setting as
 * it changes. `AppShell` mounts it around every page, so the Profile page's Appearance field and
 * the theme drawn read one value. Like the rail (D-084): the local copy paints first — `index.html`
 * already set the attribute from it — and the person's saved `ui.theme`, once read, wins.
 */

import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { fetchThemePreference, putThemePreference } from '../lib/api.js';
import { THEME_STORAGE_KEY, DARK_SCHEME_QUERY, applyTheme, deviceIsDark, readStoredTheme, resolveTheme, themeFor, type Theme, type ThemeChoice } from '../lib/theme.js';
import { browserStorage, storeChoice } from '../lib/storedFlag.js';
import { useLoad } from '../lib/useLoad.js';

export interface ThemeState {
  /** What the person chose: light, dark or Match device. */
  choice: ThemeChoice;
  /** What is drawn now. */
  theme: Theme;
  /** Applies at once, keeps the local copy and saves the preference; a failed save leaves the local copy holding it. */
  setChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeState | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => resolveTheme(readStoredTheme(browserStorage()), undefined));
  // A pick made before the saved preference has answered is the newer word; the answer does not undo it.
  const picked = useRef(false);
  const pref = useLoad((signal) => fetchThemePreference(signal));
  const remote = pref.load.state === 'ok' ? pref.load.data.value : undefined;
  useEffect(() => {
    if (remote === undefined || picked.current) return;
    const next = resolveTheme(readStoredTheme(browserStorage()), remote);
    setChoiceState(next);
    storeChoice(browserStorage(), THEME_STORAGE_KEY, next);
  }, [remote]);

  // The device's setting, followed live only while the choice is Match device.
  const [systemDark, setSystemDark] = useState<boolean>(() => deviceIsDark());
  useEffect(() => {
    if (choice !== 'system' || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_SCHEME_QUERY);
    const onChange = () => setSystemDark(query.matches);
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [choice]);

  const theme = themeFor(choice, systemDark);
  useLayoutEffect(() => applyTheme(document.documentElement, theme), [theme]);

  const value = useMemo<ThemeState>(
    () => ({
      choice,
      theme,
      setChoice: (next) => {
        picked.current = true;
        setChoiceState(next);
        storeChoice(browserStorage(), THEME_STORAGE_KEY, next);
        void putThemePreference(next).catch(() => undefined); // the local copy still holds it; the next pick tries again
      },
    }),
    [choice, theme],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** The theme and its setter; only inside `ThemeProvider` (every page in the app shell). */
export function useTheme(): ThemeState {
  const state = useContext(ThemeContext);
  if (state === undefined) throw new Error('useTheme outside ThemeProvider');
  return state;
}
